import FlagComponent from 'react-world-flags';


const LANGUAGES = {
  'rus': { flag: '🇷🇺', id: 'rus', id2: 'ru', name: 'Ru', longName: 'Russian' },
  'eng': { flag: '🇺🇸', id: 'eng', id2: 'gb', name: 'En', longName: 'English' },
  'es': { flag: '🇪🇸', id: 'es', id2: 'es', name: 'Esp', longName: 'Spanish' },
  'de': { flag: '🇩🇪', id: 'de', id2: 'de', name: 'De', longName: 'German' },
  'kz': { flag: '🇰🇿', id: 'kz', id2: 'kz', name: 'Kz', longName: 'Kazakh' },
  'zh': { flag: '🇨🇳', id: 'zh', id2: 'cn', name: 'Zh', longName: 'Chinese' },
  'ar': { flag: '🇸🇦', id: 'ar', id2: 'sa', name: 'Ar', longName: 'Arabic' }
};

export const Flag = ({ langId, height = 16, shadow = false }) => {
  return <FlagComponent
    code={LANGUAGES[langId]?.id2 || langId}
    height={height}
    style={{ display: 'inline', boxShadow: typeof shadow !== 'undefined' ? shadow ? '0 1px 3px -1px rgba(0, 0, 0, 0.8)' : 'none' : shadow }}
  />
};


export const getLanguageFlag = (langId) => {
  return LANGUAGES[langId]?.flag || '🌐';
};

export const getLanguageName = (langId) => {
  return LANGUAGES[langId]?.name || langId.toUpperCase();
};

export const getLanguageLongName = (langId) => {
  return LANGUAGES[langId]?.longName || langId;
};

// Backend/API language codes vs the canonical LANGUAGES keys used for rendering: the manag filter
// dropdown/chips emit backend codes (en/ru/ch) while our keys are eng/rus/zh. Map the aliases so a
// language filter selection resolves to the right flag/column instead of white-screening the table
// (LANGUAGES[undefined]['id'] used to throw "Cannot read properties of undefined (reading 'id')").
const LANG_ALIASES = { en: 'eng', gb: 'eng', ru: 'rus', ch: 'zh', cn: 'zh', kk: 'kz', sa: 'ar' };
export const normalizeLangId = (langId) => (langId && LANG_ALIASES[langId]) || langId;

export const getAllLanguages = (lang) => {
  if (!lang) return Object.keys(LANGUAGES);
  const key = normalizeLangId(lang);
  return [LANGUAGES[key]?.['id'] || key];
};

export const getLanguageInfo = (langId) => {
  return LANGUAGES[langId] || { flag: '🌐', name: langId.toUpperCase(), longName: langId };
};

export const isValidLanguage = (langId) => {
  return langId in LANGUAGES;
};

// API function to fetch total pages
export const fetchTotalPages = async () => {
  try {
    const response = await fetch('https://translation-manag-server.dev.torsunov.pro/projects/total_pages');
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const data = await response.json();
    return data.total_pages || 0;
  } catch (error) {
    console.error('Error fetching total pages:', error);
    return 0;
  }
};


export const calculateProjectsByLanguage = (projects, langId) => {
  if (langId === 'all') return projects.length;
  return projects.filter(project => project.parts.some(part => part.languages.some(lang => lang.id === langId))).length;
}


// Этапы работы в порядке выполнения
const WORKFLOW_STAGES = [
  { key: 'auto_translation', label: 'Перевод', shortLabel: 'Пер' },
  { key: 'translation_proofreading', label: 'Проверка перевода', shortLabel: 'ПП' },
  { key: 'voicing', label: 'Озвучка', shortLabel: 'Озв' },
  { key: 'voicing_proofreading', label: 'Проверка озвучки', shortLabel: 'ПО' },
  { key: 'animations_video', label: 'Видео', shortLabel: 'Вид' },
  { key: 'animations_video_proofreading', label: 'Проверка видео', shortLabel: 'ПВ' },
];

export const getWorkflowStages = () => WORKFLOW_STAGES;

// Вычисляет текущий этап языка для конкретной части (part)
// Возвращает индекс первого незавершённого этапа (0-5) или 6 если всё готово
const getLanguageStageForPart = (languageData) => {
  if (!languageData) return 0;

  for (let i = 0; i < WORKFLOW_STAGES.length; i++) {
    const stage = WORKFLOW_STAGES[i];
    const task = languageData[stage.key];
    if (!task || task.status !== 'done') {
      return i;
    }
  }
  return WORKFLOW_STAGES.length; // Всё готово
};

// Вычисляет текущий этап языка по всем частям проекта
// Возвращает самый отстающий этап (минимальный индекс)
// skipLastParts — количество последних частей, которые пропускаются при расчёте
export const getLanguageProgressForProject = (parts, langId, skipLastParts = 0) => {
  if (!parts || parts.length === 0) return null;

  // Сортируем части и исключаем последние N
  let activeParts = parts;
  if (skipLastParts > 0) {
    const sorted = [...parts].sort((a, b) => parseInt(a.id) - parseInt(b.id));
    activeParts = sorted.slice(0, Math.max(sorted.length - skipLastParts, 0));
  }
  if (activeParts.length === 0) return null;

  let minStage = WORKFLOW_STAGES.length; // Начинаем с "всё готово"
  let hasLanguage = false;

  activeParts.forEach(part => {
    const langData = part.languages?.find(l => l.id === langId);
    if (langData) {
      hasLanguage = true;
      const partStage = getLanguageStageForPart(langData);
      if (partStage < minStage) {
        minStage = partStage;
      }
    }
  });

  if (!hasLanguage) return null;

  // Возвращаем информацию о текущем этапе
  if (minStage >= WORKFLOW_STAGES.length) {
    return {
      stageIndex: minStage,
      stage: null,
      label: 'Готово',
      shortLabel: '✓',
      isComplete: true,
      progress: 100
    };
  }

  return {
    stageIndex: minStage,
    stage: WORKFLOW_STAGES[minStage],
    label: WORKFLOW_STAGES[minStage].label,
    shortLabel: WORKFLOW_STAGES[minStage].shortLabel,
    isComplete: false,
    progress: Math.round((minStage / WORKFLOW_STAGES.length) * 100)
  };
};

// Цвета для этапов
export const getStageColor = (stageIndex) => {
  const colors = [
    '#ff4d4f', // auto_translation - красный
    '#ff7a45', // translation_proofreading - оранжевый
    '#faad14', // voicing - жёлтый
    '#a0d911', // voicing_proofreading - лаймовый
    '#52c41a', // animations_video - зелёный
    '#13c2c2', // animations_video_proofreading - циан
  ];
  if (stageIndex >= colors.length) return '#52c41a'; // Готово - зелёный
  return colors[stageIndex];
};

// Типы задач для проверки статуса
const TASK_TYPES_FOR_STATUS = [
  'auto_translation',
  'translation_proofreading',
  'voicing',
  'voicing_proofreading',
  'full_video',
  'full_video_proofreading',
  'ts_playlist',
  'loading_in_s3'
];

/**
 * Вычисляет статус задач для конкретного языка по всем частям проекта
 * @param {Array} parts - массив частей проекта
 * @param {string} langId - ID языка
 * @returns {string} 'none' | 'in_progress' | 'done'
 *   - 'none': нет назначенных задач
 *   - 'in_progress': есть назначенные задачи, но не все выполнены
 *   - 'done': все назначенные задачи выполнены
 */
export const getLanguageTaskStatus = (parts, langId, skipLastParts = 0) => {
  if (!parts || parts.length === 0) return 'none';

  // Сортируем части и исключаем последние N
  let activeParts = parts;
  if (skipLastParts > 0) {
    const sorted = [...parts].sort((a, b) => parseInt(a.id) - parseInt(b.id));
    activeParts = sorted.slice(0, Math.max(sorted.length - skipLastParts, 0));
  }
  if (activeParts.length === 0) return 'none';

  let hasAnyTask = false;
  let allDone = true;

  activeParts.forEach(part => {
    const langData = part.languages?.find(l => l.id === langId);
    if (langData) {
      TASK_TYPES_FOR_STATUS.forEach(taskType => {
        const task = langData[taskType];
        if (task && task.assignee) {
          hasAnyTask = true;
          if (task.status !== 'done') {
            allDone = false;
          }
        }
      });
    }
  });

  if (!hasAnyTask) return 'none';
  if (allDone) return 'done';
  return 'in_progress';
};

// Helper function to format date
export const formatDate = (dateString, addTime) => {
  if (!dateString) return '-';
  try {
    const date = new Date(dateString);
    const dateOptions = {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    };

    if (addTime) {
      dateOptions.hour = '2-digit';
      dateOptions.minute = '2-digit';
    }

    return date.toLocaleDateString('ru-RU', dateOptions);
  } catch {
    return '-';
  }
};

// Helper function to format relative time (e.g., "2 дня назад", "только что")
export const formatRelativeTime = (dateString) => {
  if (!dateString) return null;
  try {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHour = Math.floor(diffMin / 60);
    const diffDay = Math.floor(diffHour / 24);
    const diffWeek = Math.floor(diffDay / 7);
    const diffMonth = Math.floor(diffDay / 30);

    // Форматируем время
    const timeStr = date.toLocaleTimeString('ru-RU', {
      hour: '2-digit',
      minute: '2-digit'
    });

    if (diffSec < 60) {
      return 'только что';
    } else if (diffMin < 60) {
      const mins = diffMin;
      if (mins === 1) return `1 мин. назад`;
      if (mins >= 2 && mins <= 4) return `${mins} мин. назад`;
      return `${mins} мин. назад`;
    } else if (diffHour < 24) {
      const hours = diffHour;
      if (hours === 1) return `1 час назад`;
      if (hours >= 2 && hours <= 4) return `${hours} часа назад`;
      return `${hours} часов назад`;
    } else if (diffDay < 7) {
      const days = diffDay;
      if (days === 1) return `вчера, ${timeStr}`;
      if (days >= 2 && days <= 4) return `${days} дня назад, ${timeStr}`;
      return `${days} дней назад, ${timeStr}`;
    } else if (diffWeek < 4) {
      const weeks = diffWeek;
      if (weeks === 1) return `неделю назад`;
      if (weeks >= 2 && weeks <= 4) return `${weeks} нед. назад`;
      return `${weeks} нед. назад`;
    } else {
      const months = diffMonth;
      if (months === 0) return '1 мес. назад';
      if (months === 1) return '1 мес. назад';
      if (months >= 2 && months <= 4) return `${months} мес. назад`;
      return `${months} мес. назад`;
    }
  } catch {
    return null;
  }
};